import type { BaseType } from 'antd/lib/typography/Base';
import { type JSX, useContext, useMemo } from 'react';

import { SelectField } from 'src/components/base/fields/SelectField';
import { TagListSelectField } from 'src/components/base/fields/TagListSelectField';
import { ReferenceContext } from 'src/components/contexts/ReferenceContext/ReferenceContext.context';
import type { ISelectOption } from 'src/types';
import type { IProductSpecificationFieldReference } from 'src/types/reference/IProductSpecificationReference';
import { SpecificationEnumDisplayMode } from 'src/types/reference/SpecificationEnumDisplayMode';

type SpecificationEnumFieldProps = {
  commonFieldProps: {
    errorType: BaseType;
    name: string;
    label?: string;
  };
  field: IProductSpecificationFieldReference;

  className?: string;
};

export function SpecificationEnumField({
  commonFieldProps,
  field,
  className,
}: Readonly<SpecificationEnumFieldProps>): JSX.Element {
  const { enumReferencesRecord } = useContext(ReferenceContext);

  const enumReference = field.enumId ? enumReferencesRecord[field.enumId] : undefined;

  const enumOptions: ISelectOption<string | null>[] = useMemo(() => {
    const fieldEnumOptions =
      enumReference?.constants?.map((constant) => ({
        value: constant.id,
        label: constant.name,
      })) ?? [];

    return field.multiselect
      ? fieldEnumOptions
      : [
          {
            value: null,
            label: field.undefinedText ?? 'N/A',
          },
          ...fieldEnumOptions,
        ];
  }, [enumReference, field.multiselect, field.undefinedText]);

  if (field.multiselect && enumReference?.displayMode !== SpecificationEnumDisplayMode.DROPDOWN) {
    return (
      <TagListSelectField
        {...commonFieldProps}
        options={enumOptions}
        multiselect={field.multiselect}
        className={className}
      />
    );
  }

  return (
    <SelectField
      {...commonFieldProps}
      className={className}
      options={enumOptions}
      mode={field.multiselect ? 'multiple' : undefined}
    />
  );
}
